import { useEffect, useRef } from "react";
import PageContainer from "@components/layout/PageContainer";
import { useNavigate } from "react-router-dom";
import ModelIndex, { ModelIndexHandle } from "@components/layout/ModelIndex";
import { faCreditCard, faEdit, faMoneyCheckDollar, faPaperPlane, faTrash, faXmark } from "@fortawesome/free-solid-svg-icons";
import { ProviderInvoiceApi, ProviderInvoiceDataType } from "@/api/ProviderInvoiceApi";
import { formatMoneyValue } from "@utils/Currency";
import { showAlert } from "@utils/Alert";
import styles from "./style.module.scss";
import { PROVIDER_INVOICE_CASH_TYPES, PROVIDER_INVOICE_TYPES } from "@utils/Constants";
import moment from "moment";
import { ProviderApi } from "@/api/ProviderApi";
import { Col, Row } from "reactstrap";
import { WithProviderInvoiceCalculateFields } from "@/formTypes/ProviderInvoiceDataType";

const ProviderInvoiceIndex = () => {
  const navigate = useNavigate();
  const modelRef = useRef<ModelIndexHandle>(null);

  useEffect(() => {
  }, []);

  const deleteRecord = async (id: string) => {
    const { errors } = await ProviderInvoiceApi.delete(id);
    if (errors?.length) {
      showAlert('danger', 'La factura del provedor no puedo ser eliminado');
    } else {
      showAlert('success', 'La factura del provedor fue eliminado correctamente');
    }
    modelRef.current?.refetch();
  }

  return (
    <PageContainer
      title="Lista Facturas de Provedores"
      backUrl="/user/dashboard-accounting"
      primaryActions={[
        {
          onClick: () => {
            navigate('/user/provider-invoices/invoice')
          },
          actionTitle: "Nuevo",
          color: "success"
        }
      ]}
    >
      <ModelIndex<ProviderInvoiceDataType>
        ref={modelRef}
        meiliConfig={{
          index: "providerinvoices",
          sort: ["fecha:desc", "numero:desc"]
        }}
        fetchData={(...args) => ProviderInvoiceApi.fetchAll(...args)}
        filterProps={[
          {
            key: "sequence",
            filterType: "contains"
          },
          {
            key: "document_key",
            filterType: "contains"
          },
          {
            key: "numero",
            filterType: "contains"
          },
          {
            key: "providerId",
            filterType: "eq",
            placeholder: "Provedor",
            options: async () => ProviderApi.optionsList()
          }
        ]}
        primaryProperties={[
          {
            key: "sequence",
            highlighted: true,
            className: styles.sequence,
            optionalHighlighted: (val: any, data: ProviderInvoiceDataType) => {
              const provider = data as WithProviderInvoiceCalculateFields<ProviderInvoiceDataType>
              if (provider.hasNotes) {
                return "hasNotes"
              } else if (provider.hasPayments) {
                return "hasPayments"
              }
              return "false"
            },
            highlightedColors: {
              "hasNotes": {
                "bg": "#d94f95",
                "text": "#fff"
              },
              "hasPayments": {
                "bg": "#eaf900",
                "text": "#000"
              },
              "false": {
                "bg": "#337ab7",
                "text": "#fff"
              }
            },
          },
          {
            key: (d) => {
              if ("providerNombre" in d) {
                return (d as any).providerNombre
              }

              return d.provider?.nombre
            },
            alignment: "Start"
          },
        ]}
        primaryPropertiesSizing={
          [
            { sm: "4", xs: "12" },
            { sm: "8", xs: "12" }
          ]
        }
        secondaryProperties={[
          {
            key: (data) => {
              const date = moment(data.fecha, "YYYY-MM-DD")
              return date.format("DD-MM-YYYY")
            },
            title: "Fecha",
          },
          {
            key: (data) => {
              return `${data.status}`
            },
          },
          {
            key: (data) => {
              return PROVIDER_INVOICE_TYPES[`${data.tipo}`.padStart(2, "0")]// ? 'Pagada' : 'Pendiente'
            },
          },
          {
            key: (data) => {
              if ("currency_id" in data) {
                return formatMoneyValue(data.total, (data as any).currency_id)
              }
              return formatMoneyValue(data.total, data.currency.currency_id)
            },
            title: "Total",
            highlighted: true
          }
        ]}
        secondaryPropertiesSizing={
          [
            { sm: "3", md: "2" },
            { sm: "3", md: "2" },
            { sm: "3", md: "2" },
            { sm: "3", md: "2" }
          ]
        }
        listActions={[
          {
            isVisible: (data) => !!data.atvMessages && data.atvMessages?.length > 0,
            href: "/user/atv-messages/listByInvoice/{:id}",
            actionTitle: "Ver Mensajes de ATV",
            iconProps: { icon: faPaperPlane }
          },
          {
            isVisible: (data) => data.tipo != PROVIDER_INVOICE_CASH_TYPES,
            href: "/user/provider-invoices/payments/{:id}",
            actionTitle: "Abonos de Facturas",
            iconProps: { icon: faMoneyCheckDollar }
          },
          {
            href: "/user/provider-invoices/notes/{:id}",
            actionTitle: "Notas de Credito/Debito",
            iconProps: { icon: faCreditCard }
          },
          {
            href: "/user/provider-invoices/invoice/{:id}",
            actionTitle: "Editar Factura de Provedores",
            iconProps: { icon: faEdit }
          },
          {
            actionTitle: "Eliminar Factura de Provedores",
            iconProps: { icon: faTrash },
            needConfirm: true,
            confirmProps: {
              title: "Eliminar Factura de Provedores",
              confirmMessage: "¿Seguro que desea eliminar este Factura de Provedores?",
              confirmBtnText: "Eliminar"
            },
            onClick: (id: string) => { deleteRecord(id) }
          }
        ]}
      >
        <Row>
          <Col xs="10" md="6">
            <div className="d-flex mb-2">
              <Col xs="1" className={styles.hasNote}>&nbsp;</Col>
              <Col className="ps-2" xs="11">Facturas con nota de credito o Debito</Col>
            </div>
          </Col>
        </Row>
        <Row>
          <Col xs="10" md="6">
            <div className="d-flex mb-2">
              <Col xs="1" className={styles.hasExtraPayment}>&nbsp;</Col>
              <Col className="ps-2" xs="11">Facturas con Abonos</Col>
            </div>
          </Col>
        </Row>
      </ModelIndex>

    </PageContainer >
  );
}

export default ProviderInvoiceIndex;
